import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import {tokenStorage} from '../app/tokenStorage';
import {API_BASE_URL} from "../app/config";
import {TOKEN_REFRESH} from "./routes/auth_routes";
import Logout from "../services/authentication/Logout";
import axiosAuth from './axiosAuth';

const axiosApi = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Access-Control-Allow-Origin": "*",
        'Content-Type': 'application/json',
        'Authorization': tokenStorage.getToken()
    },
    withCredentials: true
});

axiosApi.CancelToken = axios.CancelToken;
axiosApi.isCancel = axios.isCancel;

axiosApi.interceptors.request.use(
    config => {
        const token = tokenStorage.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        } else {
        }
        // config.headers['session-unique'] = tokenStorage.getSessionId();
        // config.headers['user-unique'] = tokenStorage.getUserId();

        // Don't override Content-Type for FormData - let axios set it automatically with boundary
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        return config;
    },
    err => Promise.reject(err)
);

axiosApi.interceptors.response.use(
    response => {
        return response;
    },
    async (error) => {
        // Handle CORS and network errors
        if (!error.response) {
            // Network error or CORS error
            // Check if it's a 401-like error (might be CORS blocking a 401 response)
            // Try to refresh token if we have a refresh token available
            if (error.config && error.config.url && !error.config.url.includes(TOKEN_REFRESH)) {
                const refresh_token = tokenStorage.getRefreshToken();
                if (refresh_token) {
                    console.log('⚠️ [Token Refresh] CORS/Network error detected, attempting token refresh...');
                    // Try to refresh token proactively
                    try {
                        const response = await axiosAuth.post(TOKEN_REFRESH, {
                            refresh_token: refresh_token
                        }, {
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            timeout: 18000 // Increased from 10 to 18 seconds to give server more time
                        });
                        
                        const accessToken = response?.data?.token || response?.data?.access_token || response?.data?.accessToken;
                        if (accessToken) {
                            tokenStorage.storeToken(accessToken);
                            axiosApi.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                            console.log('✅ [Token Refresh] Token refreshed after CORS error, retrying request');
                            // Retry the original request
                            error.config.headers['Authorization'] = `Bearer ${accessToken}`;
                            return axiosApi.request(error.config);
                        }
                    } catch (refreshError) {
                        console.error('❌ [Token Refresh] Failed to refresh after CORS error:', refreshError);
                        // Dacă backend-ul răspunde explicit cu 400/401/403/422/511,
                        // tratăm refresh token-ul ca invalid și forțăm logout-ul,
                        // ca să nu rămânem blocați într-o buclă de CORS/refresh.
                        const status = refreshError?.response?.status;
                        if ([400, 401, 403, 422, 511].includes(status)) {
                            Logout();
                        }
                    }
                }
            }
            return Promise.reject(error);
        }

        // Don't handle 401 here - let createAuthRefreshInterceptor handle it first
        // Only handle specific cases that should trigger logout immediately
        if (error.response.status === 511) {
            // User disabled
            Logout();
            return Promise.reject(error);
        }

        // If token refresh failed, logout
        if (error.config && error.config.url === TOKEN_REFRESH && [401, 403].includes(error.response.status)) {
            Logout();
            return Promise.reject(error);
        }

        // For 401 errors, let createAuthRefreshInterceptor handle them
        // It will try to refresh the token and retry the request
        console.log('📋 [Interceptor] Passing error to refresh interceptor:', error.response?.status, error.config?.url);
        return Promise.reject(error);
    },
);

const MAX_REFRESH_RETRIES = 3; // Increased from 2 to 3

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const refreshAuthLogic = async (failedRequest) => {
    try {
        console.log('🔄 [Token Refresh] Attempting to refresh token...');
        console.log('🔄 [Token Refresh] Failed request URL:', failedRequest?.config?.url);
        console.log('🔄 [Token Refresh] Failed request status:', failedRequest?.response?.status);
        
        // Don't try to refresh if the error is 511 (user disabled) - logout immediately
        if (failedRequest?.response?.status === 511) {
            console.error('❌ [Token Refresh] User disabled (511), logging out immediately');
            Logout();
            return Promise.reject(new Error('User disabled'));
        }
        
        const refresh_token = tokenStorage.getRefreshToken();
        
        if (!refresh_token) {
            console.error('❌ [Token Refresh] No refresh token available');
            Logout();
            return Promise.reject(new Error('No refresh token available'));
        }
        
        console.log('📡 [Token Refresh] Calling refresh endpoint:', TOKEN_REFRESH);
        const response = await axiosAuth.post(TOKEN_REFRESH, {
            refresh_token: refresh_token
        }, {
            headers: {
                'Content-Type': 'application/json'
            },
            timeout: 18000 // Increased from 10 to 18 seconds to give server more time
        });
        
        console.log('📥 [Token Refresh] Refresh response received');

        const accessToken = response?.data?.token || response?.data?.access_token || response?.data?.accessToken;
        const refreshToken = response?.data?.refresh_token || response?.data?.refreshToken;
        const data = response?.data;
        const userId = response?.data?.data?.user_id || response?.data?.user_id || response?.data?.userId;
        const sessionId = response?.data?.data?.session_unique || response?.data?.session_unique || response?.data?.sessionId;

        if (!accessToken) {
            console.error('❌ [Token Refresh] No access token in refresh response, logging out');
            Logout();
            return Promise.reject(new Error('No access token in refresh response'));
        }

        // Store all tokens and user data
        tokenStorage.storeToken(accessToken);
        if (refreshToken) {
            tokenStorage.storeRefreshToken(refreshToken);
        }
        if (userId) {
            tokenStorage.storeUserId(userId);
        }
        if (sessionId) {
            tokenStorage.storeSessionId(sessionId);
        }
        if (data?.firstname && data?.lastname) {
            tokenStorage.storeUserFullName(data.firstname + ' ' + data.lastname);
        }

        // Update the failed request with new token for retry
        // axios-auth-refresh will automatically retry the request after this function resolves
        // The key is to update failedRequest.config.headers, not failedRequest.response.config
        failedRequest.config.headers['Authorization'] = `Bearer ${accessToken}`;
        
        // Also update axiosApi default headers for future requests
        axiosApi.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        
        console.log('✅ [Token Refresh] Token refreshed successfully, retrying request:', failedRequest.config.url);
        return Promise.resolve();
    } catch (error) {
        console.error('❌ [Token Refresh] Error refreshing token:', error);

        // Increment retry counter on the failed request (to avoid infinite loops)
        failedRequest._refreshRetryCount = (failedRequest._refreshRetryCount || 0) + 1;

        // Handle different error scenarios
        if (error.response) {
            const status = error.response.status;
            const errorMessage = error.response?.data?.message || error.response?.data?.error || '';
            console.error(`❌ [Token Refresh] Refresh failed with status: ${status}`, errorMessage);

            // Refresh token expired, invalid, unauthorized or user disabled -> NU mai încercăm, facem logout
            if (status === 401 || status === 403 || status === 511) {
                console.error('❌ [Token Refresh] Refresh token invalid/expired or user disabled, logging out');
                Logout();
            } 
            // 400 / 422 cu mesaj „expired/invalid/refresh” -> tot logout
            else if (status === 400 || status === 422) {
                const message = (errorMessage || '').toLowerCase();
                if (message.includes('expired') || message.includes('invalid') || message.includes('refresh')) {
                    console.error(`❌ [Token Refresh] Refresh token expired/invalid (${status}), logging out`);
                    Logout();
                }
            }
            // Pentru 5xx sau alte coduri (probleme temporare de server) încercăm din nou de câteva ori
            else if (status >= 500 && status < 600 && failedRequest._refreshRetryCount <= MAX_REFRESH_RETRIES) {
                console.warn(`⚠️ [Token Refresh] Server error ${status}, retry ${failedRequest._refreshRetryCount}/${MAX_REFRESH_RETRIES}...`);
                await delay(500 * failedRequest._refreshRetryCount);
                return refreshAuthLogic(failedRequest);
            }
            // Alte coduri: nu facem logout, dar nici retry (lăsăm request-ul să pice)
        } else if (error.request) {
            // Network error - no response received
            console.error('❌ [Token Refresh] Network error - no response received');
            // În caz de network error, încercăm din nou de câteva ori
            if (failedRequest._refreshRetryCount <= MAX_REFRESH_RETRIES) {
                console.warn(`⚠️ [Token Refresh] Network error, retry ${failedRequest._refreshRetryCount}/${MAX_REFRESH_RETRIES}...`);
                await delay(500 * failedRequest._refreshRetryCount);
                return refreshAuthLogic(failedRequest);
            }
        } else {
            console.error('❌ [Token Refresh] Unknown error:', error.message);
            // Pentru erori necunoscute nu facem logout; nu retry dacă deja am încercat de mai multe ori
        }

        return Promise.reject(error);
    }
}

// Configure auth refresh interceptor
// This will automatically retry failed requests after token refresh
// When a 401/403 is received, refreshAuthLogic is called
// After it resolves, the original request is automatically retried with the new token
createAuthRefreshInterceptor(axiosApi, refreshAuthLogic, {
    statusCodes: [401, 403], // Retry on 401 and 403
    retryInstance: axiosApi, // Use the same axios instance for retry
    skipWhileRefreshing: false, // Don't skip requests while refreshing - queue them
    onRetry: (requestConfig) => {
        // This callback is called before retrying the request
        // Ensure the latest token is used
        const token = tokenStorage.getToken();
        if (token) {
            requestConfig.headers['Authorization'] = `Bearer ${token}`;
        }
        console.log('🔄 [Token Refresh] Retrying request:', requestConfig.url || (requestConfig.baseURL + requestConfig.url));
        return requestConfig;
    }
})

// Add additional logging to verify interceptor is working
console.log('✅ [Token Refresh] Auth refresh interceptor configured');

export default axiosApi;
